import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

/**
 * Search Filter Component
 * 
 * Search input with debounced output for filtering agents.
 * Includes search icon and optional clear button.
 * 
 * Features:
 * - Debounced search (300ms default)
 * - Clear button when input has value
 * - Search icon indicator
 * - Keyboard accessibility
 * 
 * Usage:
 * <app-search-filter
 *   [placeholder]="'Search by keyword...'"
 *   [debounceMs]="300"
 *   (searchChanged)="onSearch($event)">
 * </app-search-filter>
 */
@Component({
  selector: 'app-search-filter',
  templateUrl: './search-filter.component.html',
  styleUrls: ['./search-filter.component.scss']
})
export class SearchFilterComponent implements OnInit, OnDestroy {
  /**
   * Placeholder text for the search input
   */
  @Input() placeholder: string = 'Search by keyword or describe your requirement I\'ll guide you to the right agent';

  /**
   * Debounce delay in milliseconds
   */
  @Input() debounceMs: number = 300;

  /**
   * Initial search value
   */
  @Input() initialValue: string = '';

  /**
   * Emits debounced search query string
   */
  @Output() searchChanged = new EventEmitter<string>();

  /** Current search query value */
  searchQuery: string = '';

  /** Subject for debouncing input */
  private readonly searchSubject = new Subject<string>();

  /** Subject for cleanup on destroy */
  private readonly destroy$ = new Subject<void>();

  /**
   * Initialize component and set up debounced search
   */
  ngOnInit(): void {
    // Set initial value if provided
    if (this.initialValue) {
      this.searchQuery = this.initialValue;
    }

    // Set up debounced search emission
    this.searchSubject.pipe(
      debounceTime(this.debounceMs),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.searchChanged.emit(query);
    });
  }

  /**
   * Clean up subscriptions on destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle input change
   * 
   * @param event - Input event
   */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;
    this.searchSubject.next(this.searchQuery);
  }

  /**
   * Clear the search input
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.searchSubject.next('');
  }

  /**
   * Handle keyboard events
   * 
   * @param event - Keyboard event
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.clearSearch();
    }
  }

  /**
   * Check if search input has a value
   * 
   * @returns boolean - True if input has value
   */
  get hasValue(): boolean {
    return this.searchQuery.trim().length > 0;
  }
}
